import React, { useEffect, useMemo, useState } from "react";

const SITES = [
  { key: "VIG", label: "vigliettisport.com", color: "#174f88" },
  { key: "BOT", label: "botteroski.com", color: "#8b1e1e" },
];

const emptyDraft = (modaFields) => ({
  moda: Object.fromEntries(modaFields.map((field) => [field.key, ""])),
  selectedSites: { VIG: false, BOT: false },
  requirementsBySite: { VIG: [], BOT: [] },
});

const requirementKey = (field) =>
  `${field.source}:${field.source === "moda" ? field.field : field.path}`;

const requirementPayload = (field) => ({
  source: field.source,
  ...(field.source === "moda" ? { field: field.field } : { path: field.path }),
  label: field.label,
});

function WebCategoryRequirementCreateModal({
  open,
  onClose,
  onSave,
  modaFields,
  mongoFields,
  lookupOptions,
  buttonClassesGreen,
  buttonClassesWhite,
  saving,
}) {
  const [draft, setDraft] = useState(() => emptyDraft(modaFields));
  const [error, setError] = useState("");

  const requirementFields = useMemo(
    () => [
      ...modaFields.map((field) => ({
        key: `moda:${field.key}`,
        source: "moda",
        field: field.key,
        label: field.label,
      })),
      ...mongoFields.map((field) => ({
        key: `mongo:${field.path}`,
        source: "mongo",
        path: field.path,
        label: field.label,
      })),
    ],
    [modaFields, mongoFields],
  );

  useEffect(() => {
    if (!open) return;
    setDraft(emptyDraft(modaFields));
    setError("");
  }, [modaFields, open]);

  if (!open) return null;

  const updateModa = (fieldKey, value) => {
    setDraft((current) => ({
      ...current,
      moda: { ...current.moda, [fieldKey]: value },
      requirementsBySite: value
        ? Object.fromEntries(
            SITES.map((site) => [
              site.key,
              current.requirementsBySite[site.key].filter(
                (requirement) => requirementKey(requirement) !== `moda:${fieldKey}`,
              ),
            ]),
          )
        : current.requirementsBySite,
    }));
  };

  const toggleSite = (siteKey) => {
    setDraft((current) => ({
      ...current,
      selectedSites: {
        ...current.selectedSites,
        [siteKey]: !current.selectedSites[siteKey],
      },
    }));
  };

  const toggleRequirement = (siteKey, field) => {
    if (field.source === "moda" && draft.moda[field.field]) return;
    const key = requirementKey(field);
    setDraft((current) => {
      const currentRequirements = current.requirementsBySite[siteKey] || [];
      const selected = currentRequirements.some(
        (requirement) => requirementKey(requirement) === key,
      );
      return {
        ...current,
        requirementsBySite: {
          ...current.requirementsBySite,
          [siteKey]: selected
            ? currentRequirements.filter(
                (requirement) => requirementKey(requirement) !== key,
              )
            : [...currentRequirements, requirementPayload(field)],
        },
      };
    });
  };

  const submit = async () => {
    const moda = Object.fromEntries(
      modaFields.map((field) => [field.key, String(draft.moda[field.key] || "").trim()]),
    );
    if (!Object.values(moda).some(Boolean)) {
      setError("Seleziona almeno un filtro MODA di partenza.");
      return;
    }
    const sites = SITES.map((site) => site.key).filter(
      (site) => draft.selectedSites[site],
    );
    if (!sites.length) {
      setError("Seleziona almeno un sito.");
      return;
    }
    const siteWithoutRequirements = sites.find(
      (site) => !draft.requirementsBySite[site]?.length,
    );
    if (siteWithoutRequirements) {
      setError(`Indica almeno un dato integrativo per ${siteWithoutRequirements}.`);
      return;
    }

    setError("");
    try {
      await onSave({
        moda,
        sites,
        requirementsBySite: Object.fromEntries(
          sites.map((site) => [site, draft.requirementsBySite[site]]),
        ),
      });
    } catch (saveError) {
      setError(
        saveError?.response?.data?.error ||
          saveError.message ||
          "Errore salvataggio non sufficienza",
      );
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-5 sfondoOverlayTema flex items-center justify-center">
      <div
        className="sfondoBiancoTema black-80 w-90 pa4 br2 shadow-5"
        style={{ maxWidth: "70rem", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="flex justify-between items-start mb3">
          <h3 className="ma0">Nuova non sufficienza</h3>
          <button
            type="button"
            className={buttonClassesWhite}
            onClick={onClose}
            disabled={saving}
          >
            Chiudi
          </button>
        </div>

        {error && <div className="pa3 mb3 bg-washed-red dark-red">{error}</div>}

        <section className="pb3 mb3 bb b--black-10">
          <h4 className="mt0 mb2">Filtri di partenza non sufficienti</h4>
          <div className="flex flex-wrap nl2 nr2">
            {modaFields.map((field) => (
              <label key={field.key} className="db w-100 w-50-m w-20-l ph2 mb2">
                <span className="db f7 fw6 mb1">{field.label}</span>
                <select
                  className="ba b--black-20 pa2 br2 w-100 bg-white"
                  value={draft.moda[field.key]}
                  onChange={(event) => updateModa(field.key, event.target.value)}
                >
                  <option value="">Qualsiasi valore</option>
                  {(lookupOptions[field.key] || []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.description === option.code
                        ? option.description
                        : `${option.description} (${option.code})`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>

        <section>
          <h4 className="mt0 mb2">Dati integrativi richiesti</h4>
          {SITES.map((site) => {
            const selected = draft.selectedSites[site.key];
            const selectedRequirements = draft.requirementsBySite[site.key] || [];
            return (
              <div key={site.key} className="pb3 mb3 bb b--black-10">
                <label className="flex items-center pointer fw6 mb2">
                  <input
                    type="checkbox"
                    className="mr2"
                    checked={selected}
                    onChange={() => toggleSite(site.key)}
                  />
                  <span style={{ color: site.color }}>{site.label}</span>
                </label>
                {selected && (
                  <div className="flex flex-wrap pl3">
                    {requirementFields.map((field) => {
                      const alreadyDefined =
                        field.source === "moda" && Boolean(draft.moda[field.field]);
                      const checked = selectedRequirements.some(
                        (requirement) =>
                          requirementKey(requirement) === requirementKey(field),
                      );
                      return alreadyDefined ? (
                        <div key={`${site.key}-${field.key}`} className="w-100 w-50-l pr2 mb2 f6">
                          <div className="pa2 br2 bg-washed-green dark-green ba b--green">
                            <span className="fw6">{field.label}</span>
                            <span className="ml2 f7">Gia nel contesto</span>
                          </div>
                        </div>
                      ) : (
                        <label
                          key={`${site.key}-${field.key}`}
                          className="w-100 w-50-l pr2 mb2 pointer f6"
                        >
                          <input
                            type="checkbox"
                            className="mr2"
                            checked={checked}
                            onChange={() => toggleRequirement(site.key, field)}
                          />
                          {field.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        <div className="flex justify-end mt3">
          <button
            type="button"
            className={buttonClassesWhite + " mr2"}
            onClick={onClose}
            disabled={saving}
          >
            Annulla
          </button>
          <button
            type="button"
            className={buttonClassesGreen}
            onClick={submit}
            disabled={saving}
          >
            Salva non sufficienza
          </button>
        </div>
      </div>
    </div>
  );
}

export default WebCategoryRequirementCreateModal;
